// Shared tiny helpers for the MCO modules (ported from tilley-calculators/src/api/rmaAdm.js).
// Read-only helpers only — never invent rates.

export const num = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Same coercion, but a missing value stays missing. Display fields must use this: a column RMA
// has not determined reads as "not yet determined", never as $0.00.
export const numOrNull = (v) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Crop share reaches MCO as a decimal (budgetCountyPractice.share_percent = 1.00) from some
 * callers and as a percent (100) from others. Normalize to a 0-1 decimal.
 * 1 is read as 100%, not 1% — RMA shares run 0.5-1.0 and a 1% share is not a real policy.
 */
export function toShareDecimal(v, fallback = 1) {
  const n = num(v);
  if (!(n > 0)) return fallback;
  return n > 1 ? n / 100 : n;
}

/** Prefer first present key (snake / Pascal / toolkit quirks). */
export function pick(row, ...keys) {
  if (!row) return undefined;
  for (const k of keys) {
    if (row[k] != null && row[k] !== "") return row[k];
  }
  return undefined;
}

/** Copy shown when electing MCO / area supplements before a base crop plan is available. */
export const UNDERLYING_REQUIREMENT_DISCLAIMER =
  "Requires an underlying MPCI policy (Yield Protection, Revenue Protection, Revenue Protection with Harvest Price Exclusion, or Actual Production History). You may elect this supplement before the underlying policy sales closing date.";

/** MCO InsuranceOffer practice_name values in ADM (RMA uvw_ix_InsuranceOffer — never null/omitted). */
export const MCO_ADM_PRACTICE_NAMES = ["Irrigated", "Non-Irrigated"];

/** Map farm/budget practice label → ADM MCO practice_name. */
export function resolveMcoPracticeName(practiceName) {
  const p = String(practiceName || "").trim();
  if (!p) return null;
  if (/non[- ]?irrigat/i.test(p)) return "Non-Irrigated";
  if (/irrigat/i.test(p)) return "Irrigated";
  if (p === "Non-Irrigated" || p === "Irrigated") return p;
  return null;
}

/** Explicit ADM practice first; if unknown/missing try both Irrigated and Non-Irrigated. */
export function mcoPracticeCandidates(practiceName) {
  const resolved = resolveMcoPracticeName(practiceName);
  return resolved ? [resolved] : [...MCO_ADM_PRACTICE_NAMES];
}

/** RMA handbook bands when ADM AreaRate is not published yet (early MCO SCD). */
export function defaultMcoBandsForYear(reinsuranceYear) {
  const y = num(reinsuranceYear);
  if (y >= 2027) {
    return [{ order: "1", start: 0.95, end: 0.9 }];
  }
  return [
    { order: "1", start: 0.95, end: 0.86 },
    { order: "2", start: 0.9, end: 0.86 },
  ];
}

/**
 * Copy an optimizeInsurance() combo so MCO can mutate it. structuredClone() cannot be used:
 * combos carry functions and throw DataCloneError. Only the layers MCO writes to need new
 * objects — the combo itself, comboStressTestResults, and each stress cell.
 */
export function cloneComboForMco(combo) {
  const results = combo?.comboStressTestResults?.results || [];
  return {
    ...combo,
    comboStressTestResults: {
      ...combo?.comboStressTestResults,
      results: results.map((row) => (Array.isArray(row) ? row.map((cell) => ({ ...cell })) : { ...row })),
    },
  };
}

/** Case/separator-insensitive pick — Watts Official/Mirror rows only normalize top-level casing. */
export function pickRow(row, ...keys) {
  if (!row) return undefined;
  const lower = {};
  for (const [k, v] of Object.entries(row)) {
    lower[String(k).toLowerCase().replaceAll(" ", "_").replaceAll("-", "_")] = v;
  }
  for (const k of keys) {
    const v = lower[String(k).toLowerCase().replaceAll(" ", "_").replaceAll("-", "_")];
    if (v != null && v !== "") return v;
  }
  return undefined;
}
